import { useEffect, useState } from 'react';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import Field from '../../components/common/Field';
import Avatar from '../../components/common/Avatar';
import Spinner from '../../components/common/Spinner';
import EmptyState from '../../components/common/EmptyState';
import MemberInfoModal from './MemberInfoModal';
import AdminRoleLines, { hasAdminRole } from './AdminRoleLines';
import roomParentsService from '../../api/services/roomParents.service';
import useRoomParentsStore from '../../store/useRoomParentsStore';
import useParentsStore from '../../store/useParentsStore';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import { showToast } from '../../store/useToastStore';
import { grantRoomParentAccess } from '../../store/actions';
import { fmtDateTime } from '../../utils/formatters';
import { avatarFor } from '../../utils/avatar';

const CUSTOM = '__custom__';
const MEMBERS_PAGE_SIZE = 10;

export default function RoomParentsPage() {
  const activityLog = useRoomParentsStore((s) => s.activityLog);
  const parents = useParentsStore((s) => s.parents);
  const contexts = useContextStore((s) => s.contexts);
  const ctx = useContextStore(selectCurrentContext);
  const classContexts = contexts.filter((c) => c.type === 'class');

  /* Live members via getGroupMember (type=all, id=schoolId) with server-side
     pagination — the only source for this list; no local/mock fallback. */
  const schoolId = ctx?.schoolId || null;
  const [members, setMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberPage, setMemberPage] = useState(1);
  const [memberTotal, setMemberTotal] = useState(0);
  const [memberTotalPages, setMemberTotalPages] = useState(1);
  const [openMember, setOpenMember] = useState(null);

  useEffect(() => {
    setMemberPage(1);
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return undefined;
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      try {
        const result = await roomParentsService.getGroupMembers(schoolId, {
          type: 'all',
          page: memberPage,
          limit: MEMBERS_PAGE_SIZE,
        });
        if (!cancelled) {
          setMembers(result.members);
          setMemberTotal(result.total);
          setMemberTotalPages(result.totalPages);
        }
      } catch (err) {
        if (!cancelled) showToast(err.message);
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId, memberPage]);

  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [customName, setCustomName] = useState('');
  const [className, setClassName] = useState(classContexts[0]?.name || '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  const available = parents;

  const handleNameSelect = (val) => {
    setSelectedId(val);
    if (val === CUSTOM) {
      setCustomName('');
      setPhone('');
      setEmail('');
    } else {
      const p = parents.find((x) => x.id === val);
      setPhone(p?.phone || '');
      setEmail(p?.email || '');
    }
  };

  const closeForm = () => {
    setFormOpen(false);
    setSelectedId('');
    setCustomName('');
    setPhone('');
    setEmail('');
  };

  const grantAccess = () => {
    const ok = grantRoomParentAccess({
      parentId: selectedId && selectedId !== CUSTOM ? selectedId : null,
      customName,
      className,
      phone: phone.trim(),
      email: email.trim(),
    });
    if (ok) closeForm();
  };

  const sortedLog = [...activityLog].sort((a, b) => b.when.localeCompare(a.when));

  return (
    <div className="sec on">
      <SectionHead
        title="Room Parents & Activity"
        sub={`Everyone with access to the ${ctx?.schoolName || ctx?.name || 'school'} Master Page, and a log of who's updated the calendar or lunch menu.`}
      />

      <Card
        title="Room Parents with Access"
        action={<button className="btn btn-primary btn-sm" onClick={() => setFormOpen((o) => !o)}>+ Add Room Parent</button>}
      >
        <div className={`inline-form ${formOpen ? 'on' : ''}`}>
          <div className="form-grid">
            <Field label="Name" style={{ gridColumn: '1/-1' }}>
              <select value={selectedId} onChange={(e) => handleNameSelect(e.target.value)}>
                <option value="">— Select an existing parent —</option>
                {available.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
                <option value={CUSTOM}>✏️ Type a new name…</option>
              </select>
              {selectedId === CUSTOM && (
                <input
                  type="text"
                  value={customName}
                  placeholder="Full name"
                  style={{ marginTop: 8 }}
                  autoFocus
                  onChange={(e) => setCustomName(e.target.value)}
                />
              )}
            </Field>
            <Field label="Class">
              <select value={className} onChange={(e) => setClassName(e.target.value)}>
                {classContexts.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Phone (optional)">
              <input type="tel" value={phone} placeholder="(512) 555-0100" onChange={(e) => setPhone(e.target.value)} />
            </Field>
            <Field label="Email (optional)">
              <input type="text" value={email} placeholder="parent@email.com" onChange={(e) => setEmail(e.target.value)} />
            </Field>
          </div>
          <div className="inline-form-actions">
            <button className="btn btn-ghost" onClick={closeForm}>Cancel</button>
            <button className="btn btn-primary" onClick={grantAccess}>Grant Access</button>
          </div>
        </div>

        <div>
            {membersLoading && (
              <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
                <Spinner variant="green" /> Loading members…
              </div>
            )}
            {!membersLoading && (!members || members.length === 0) && (
              <EmptyState>No parents found.</EmptyState>
            )}
            {!membersLoading &&
              (members || []).map((m) => {
                const av = avatarFor(m.name, []);
                return (
                  <div className="ev-row-mini" key={m.id} onClick={() => setOpenMember(m)}>
                    <Avatar src={m.picture} initials={av.initials} color={av.color} size={36} fontSize={12} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="ev-title">{m.name}</div>
                      {/* admins: the classes / sub-groups they run; everyone else:
                          what they've joined */}
                      {hasAdminRole(m) ? (
                        <AdminRoleLines member={m} />
                      ) : (
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{m.group || m.phone || m.email || '—'}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            {memberTotalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  {memberTotal} member{memberTotal === 1 ? '' : 's'} · Page {memberPage} of {memberTotalPages}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={memberPage <= 1 || membersLoading}
                    onClick={() => setMemberPage((p) => p - 1)}
                  >
                    ← Prev
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={memberPage >= memberTotalPages || membersLoading}
                    onClick={() => setMemberPage((p) => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
        </div>
      </Card>

      <Card title="Activity Log">
        <div>
          {sortedLog.length ? (
            sortedLog.map((a, i) => (
              <div className="ev-row-mini" style={{ cursor: 'default' }} key={i}>
                <div className="avatar" style={{ width: 32, height: 32, fontSize: 12, background: 'var(--green-light)', color: 'var(--green)' }}>📝</div>
                <div style={{ flex: 1 }}>
                  <div className="ev-title">{a.who} — {a.action}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{a.detail} · {fmtDateTime(a.when)}</div>
                </div>
              </div>
            ))
          ) : (
            <EmptyState>No activity yet.</EmptyState>
          )}
        </div>
      </Card>

      <MemberInfoModal member={openMember} onClose={() => setOpenMember(null)} />
    </div>
  );
}
